import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import AddressAutocomplete from '../../components/ui/address-autocomplete';
import { 
  ArrowLeft, ArrowRight, User, UserPlus, Search, MapPin, Calendar,
  Check, Building, Home, Package, FileText, Truck
} from 'lucide-react';
import api from '../../utils/api';

const CreateJobPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const preselectedCustomerId = searchParams.get('customerId');
  
  // Flow state
  const [step, setStep] = useState(preselectedCustomerId ? 2 : 1); // 1: Select Customer, 2: Job Details
  const [loading, setLoading] = useState(false);
  
  // Customer selection state
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [customerType, setCustomerType] = useState('all');
  
  // New customer form
  const [newCustomer, setNewCustomer] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    customer_type: 'domestic',
    address: {
      street: '',
      city: '',
      postal_code: '',
      country: 'UK'
    }
  });
  
  // Job form state
  const [jobData, setJobData] = useState({
    customer_type: 'domestic',
    from_address: {
      street: '',
      city: '',
      postal_code: '',
      country: 'UK',
      floor: '',
      lift_available: false,
      parking_info: '',
      property_type: '',
      bedrooms: '',
      has_stairs: false,
      stairs_count: '',
      tight_access: false,
      parking_available: false,
      parking_suspension: false,
      vehicle_restrictions: false,
      walk_distance: ''
    },
    to_address: {
      street: '',
      city: '',
      postal_code: '',
      country: 'UK',
      floor: '',
      lift_available: false,
      parking_info: '',
      property_type: '',
      bedrooms: '',
      has_stairs: false,
      stairs_count: '',
      tight_access: false,
      parking_available: false,
      parking_suspension: false,
      vehicle_restrictions: false,
      walk_distance: ''
    },
    move_out_date: '',
    move_in_date: '',
    flexible_dates: false,
    preferred_time: '',
    same_day_move: false,
    time_restrictions: false,
    time_restrictions_notes: '',
    special_instructions: '',
    // Occupancy
    property_packed: false,
    someone_present: true,
    keys_codes_required: false,
    keys_codes_notes: '',
    utilities_accessible: true,
    // Services
    requires_packing: false,
    requires_packing_materials: false,
    requires_dismantling: false,
    requires_storage: false,
    requires_waste_removal: false,
    requires_end_of_tenancy: false,
    requires_cleaning: false,
    property_details: {
      bedrooms: '',
      property_type: '',
      access_notes: ''
    }
  });

  useEffect(() => {
    fetchCustomers();
    if (preselectedCustomerId) {
      fetchCustomerById(preselectedCustomerId);
    }
  }, [preselectedCustomerId]);

  const fetchCustomers = async () => {
    try {
      // Use /users/list endpoint and filter customers client-side
      const response = await api.get('/users/list');
      const allUsers = response.data || [];
      // Filter for customer roles only
      const customerUsers = allUsers.filter(u => 
        u.role === 'customer' || u.role === 'customer_commercial'
      );
      setCustomers(customerUsers);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      toast.error('Failed to load customers');
    }
  };

  const fetchCustomerById = async (id) => {
    try {
      const response = await api.get(`/users/${id}`);
      if (response.data) {
        setSelectedCustomer(response.data);
        // Pre-fill from address from customer's address
        if (response.data.address) {
          setJobData(prev => ({
            ...prev,
            from_address: { ...prev.from_address, ...response.data.address },
            customer_type: response.data.role === 'customer_commercial' ? 'commercial' : 'domestic'
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch customer:', error);
    }
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    // Pre-fill from address if customer has one
    if (customer.address) {
      setJobData(prev => ({
        ...prev,
        from_address: { ...prev.from_address, ...customer.address },
        customer_type: customer.role === 'customer_commercial' ? 'commercial' : 'domestic'
      }));
    }
    setStep(2);
  };

  const handleCreateCustomer = async () => {
    if (!newCustomer.first_name || !newCustomer.last_name) {
      toast.error('Please enter customer name');
      return;
    }
    if (!newCustomer.email && !newCustomer.phone) {
      toast.error('Please enter email or phone');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/users/create-customer', {
        first_name: newCustomer.first_name,
        last_name: newCustomer.last_name,
        email: newCustomer.email,
        phone: newCustomer.phone,
        role: newCustomer.customer_type === 'commercial' ? 'customer_commercial' : 'customer',
        address: newCustomer.address
      });
      
      toast.success('Customer created successfully');
      setSelectedCustomer(response.data);
      setShowNewCustomer(false);
      
      // Pre-fill from address
      if (newCustomer.address) {
        setJobData(prev => ({
          ...prev,
          from_address: { ...prev.from_address, ...newCustomer.address },
          customer_type: newCustomer.customer_type
        }));
      }
      
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJob = async () => {
    if (!selectedCustomer) {
      toast.error('Please select a customer first');
      setStep(1);
      return;
    }

    if (!jobData.from_address.street || !jobData.from_address.city) {
      toast.error('Please enter the collection address');
      return;
    }

    if (!jobData.to_address.street || !jobData.to_address.city) {
      toast.error('Please enter the delivery address');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/jobs/create-for-customer', {
        customer_id: selectedCustomer.id,
        customer_type: jobData.customer_type,
        from_address: jobData.from_address,
        to_address: jobData.to_address,
        move_out_date: jobData.move_out_date || null,
        move_in_date: jobData.move_in_date || null,
        flexible_dates: jobData.flexible_dates,
        preferred_time: jobData.preferred_time,
        same_day_move: jobData.same_day_move,
        time_restrictions: jobData.time_restrictions,
        time_restrictions_notes: jobData.time_restrictions_notes,
        special_instructions: jobData.special_instructions,
        property_details: jobData.property_details,
        // Occupancy
        occupancy: {
          property_packed: jobData.property_packed,
          someone_present: jobData.someone_present,
          keys_codes_required: jobData.keys_codes_required,
          keys_codes_notes: jobData.keys_codes_notes,
          utilities_accessible: jobData.utilities_accessible,
        },
        // Services
        services: {
          packing: jobData.requires_packing,
          packing_materials: jobData.requires_packing_materials,
          dismantling: jobData.requires_dismantling,
          storage: jobData.requires_storage,
          waste_removal: jobData.requires_waste_removal,
          end_of_tenancy: jobData.requires_end_of_tenancy,
          cleaning: jobData.requires_cleaning,
        }
      });
      
      toast.success('Job created successfully!');
      navigate(`/control-panel/jobs`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(c => {
    const fullName = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
    const email = (c.email || '').toLowerCase();
    const phone = c.phone || '';
    const matchesSearch = fullName.includes(searchTerm.toLowerCase()) || 
                          email.includes(searchTerm.toLowerCase()) ||
                          phone.includes(searchTerm);
    const matchesType = customerType === 'all' || 
                        (customerType === 'domestic' && c.role === 'customer') ||
                        (customerType === 'commercial' && c.role === 'customer_commercial');
    return matchesSearch && matchesType;
  });

  return (
    <div className="space-y-6" data-testid="create-job-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate('/control-panel/jobs')} data-testid="back-btn">
          <ArrowLeft size={16} className="mr-2" />
          {t('common.back')}
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{t('jobs.create_job')}</h1>
          <p className="text-gray-600">Step {step} of 2: {step === 1 ? 'Select Customer' : 'Job Details'}</p>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="flex items-center gap-4 mb-6">
        <div className={`flex items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-primary text-white' : 'bg-gray-200'}`}>
            {step > 1 ? <Check size={16} /> : '1'}
          </div>
          <span className="font-medium">Select Customer</span>
        </div>
        <div className="flex-1 h-1 bg-gray-200 rounded">
          <div className={`h-full bg-primary rounded transition-all ${step >= 2 ? 'w-full' : 'w-0'}`} />
        </div>
        <div className={`flex items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-primary text-white' : 'bg-gray-200'}`}>
            2
          </div>
          <span className="font-medium">{t('jobs.job_details')}</span>
        </div>
      </div>

      {/* Step 1: Customer Selection */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="text-primary" />
              Select or Create Customer
            </CardTitle>
            <CardDescription>
              A job must be linked to a customer. Select an existing customer or create a new one.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Search and Filter */}
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  placeholder="Search by name, email or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="customer-search"
                />
              </div>
              <Select value={customerType} onValueChange={setCustomerType}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="domestic">Domestic</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => setShowNewCustomer(true)} data-testid="new-customer-btn">
                <UserPlus size={16} className="mr-2" />
                New Customer
              </Button>
            </div>

            {/* Customer List */}
            <ScrollArea className="h-[400px] border rounded-lg">
              {filteredCustomers.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <User size={48} className="mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No customers found</p>
                  <p className="text-sm">Try a different search or create a new customer</p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredCustomers.map((customer) => (
                    <div
                      key={customer.id}
                      onClick={() => handleSelectCustomer(customer)}
                      className="p-4 cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between"
                      data-testid={`customer-${customer.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          customer.role === 'customer_commercial' ? 'bg-indigo-100 text-indigo-600' : 'bg-blue-100 text-blue-600'
                        }`}>
                          {customer.role === 'customer_commercial' ? <Building size={20} /> : <Home size={20} />}
                        </div>
                        <div>
                          <p className="font-semibold">{customer.first_name} {customer.last_name}</p>
                          <p className="text-sm text-gray-500">{customer.email || customer.phone}</p>
                          {customer.address?.city && (
                            <p className="text-xs text-gray-400 flex items-center gap-1">
                              <MapPin size={12} />
                              {customer.address.city}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">
                          {customer.role === 'customer_commercial' ? 'Commercial' : 'Domestic'}
                        </Badge>
                        <ArrowRight size={16} className="text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Job Details */}
      {step === 2 && selectedCustomer && (
        <div className="space-y-6">
          {/* Selected Customer Card */}
          <Card className="border-primary bg-primary/5">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    selectedCustomer.role === 'customer_commercial' ? 'bg-indigo-100 text-indigo-600' : 'bg-blue-100 text-blue-600'
                  }`}>
                    {selectedCustomer.role === 'customer_commercial' ? <Building size={20} /> : <Home size={20} />}
                  </div>
                  <div>
                    <p className="font-semibold">{selectedCustomer.first_name} {selectedCustomer.last_name}</p>
                    <p className="text-sm text-gray-600">{selectedCustomer.email || selectedCustomer.phone}</p>
                  </div>
                </div>
                <Button variant="outline" onClick={() => setStep(1)} size="sm">
                  Change Customer
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Job Details Form */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Collection Address */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Package className="text-orange-500" />
                  {t('jobs.collection')} Address
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Street Address *</Label>
                  <AddressAutocomplete
                    value={jobData.from_address.street}
                    onAddressSelect={(addr) => setJobData(prev => ({
                      ...prev,
                      from_address: { 
                        ...prev.from_address, 
                        street: addr.street,
                        city: addr.city || prev.from_address.city,
                        postal_code: addr.postal_code || prev.from_address.postal_code
                      }
                    }))}
                    onChange={(e) => setJobData(prev => ({
                      ...prev,
                      from_address: { ...prev.from_address, street: e.target.value }
                    }))}
                    placeholder="Start typing address..."
                    data-testid="from-street"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>City *</Label>
                    <Input
                      value={jobData.from_address.city}
                      onChange={(e) => setJobData(prev => ({
                        ...prev,
                        from_address: { ...prev.from_address, city: e.target.value }
                      }))}
                      placeholder="London"
                      data-testid="from-city"
                    />
                  </div>
                  <div>
                    <Label>Postal Code</Label>
                    <Input
                      value={jobData.from_address.postal_code}
                      onChange={(e) => setJobData(prev => ({
                        ...prev,
                        from_address: { ...prev.from_address, postal_code: e.target.value }
                      }))}
                      placeholder="SW1A 1AA"
                      data-testid="from-postal"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Floor</Label>
                    <Input
                      value={jobData.from_address.floor}
                      onChange={(e) => setJobData(prev => ({
                        ...prev,
                        from_address: { ...prev.from_address, floor: e.target.value }
                      }))}
                      placeholder="Ground, 1st, etc."
                    />
                  </div>
                  <div>
                    <Label>Lift Available</Label>
                    <Select 
                      value={jobData.from_address.lift_available ? 'yes' : 'no'}
                      onValueChange={(v) => setJobData(prev => ({
                        ...prev,
                        from_address: { ...prev.from_address, lift_available: v === 'yes' }
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Parking Info</Label>
                  <Input
                    value={jobData.from_address.parking_info}
                    onChange={(e) => setJobData(prev => ({
                      ...prev,
                      from_address: { ...prev.from_address, parking_info: e.target.value }
                    }))}
                    placeholder="Street parking, driveway, etc."
                  />
                </div>
              </CardContent>
            </Card>

            {/* Delivery Address */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Truck className="text-green-500" />
                  {t('jobs.delivery')} Address
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Street Address *</Label>
                  <AddressAutocomplete
                    value={jobData.to_address.street}
                    onAddressSelect={(addr) => setJobData(prev => ({
                      ...prev,
                      to_address: { 
                        ...prev.to_address, 
                        street: addr.street,
                        city: addr.city || prev.to_address.city,
                        postal_code: addr.postal_code || prev.to_address.postal_code
                      }
                    }))}
                    onChange={(e) => setJobData(prev => ({
                      ...prev,
                      to_address: { ...prev.to_address, street: e.target.value }
                    }))}
                    placeholder="Start typing delivery address..."
                    data-testid="to-street"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>City *</Label>
                    <Input
                      value={jobData.to_address.city}
                      onChange={(e) => setJobData(prev => ({
                        ...prev,
                        to_address: { ...prev.to_address, city: e.target.value }
                      }))}
                      placeholder="Manchester"
                      data-testid="to-city"
                    />
                  </div>
                  <div>
                    <Label>Postal Code</Label>
                    <Input
                      value={jobData.to_address.postal_code}
                      onChange={(e) => setJobData(prev => ({
                        ...prev,
                        to_address: { ...prev.to_address, postal_code: e.target.value }
                      }))}
                      placeholder="M1 1AA"
                      data-testid="to-postal"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Floor</Label>
                    <Input
                      value={jobData.to_address.floor}
                      onChange={(e) => setJobData(prev => ({
                        ...prev,
                        to_address: { ...prev.to_address, floor: e.target.value }
                      }))}
                      placeholder="Ground, 1st, etc."
                    />
                  </div>
                  <div>
                    <Label>Lift Available</Label>
                    <Select 
                      value={jobData.to_address.lift_available ? 'yes' : 'no'}
                      onValueChange={(v) => setJobData(prev => ({
                        ...prev,
                        to_address: { ...prev.to_address, lift_available: v === 'yes' }
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Parking Info</Label>
                  <Input
                    value={jobData.to_address.parking_info}
                    onChange={(e) => setJobData(prev => ({
                      ...prev,
                      to_address: { ...prev.to_address, parking_info: e.target.value }
                    }))}
                    placeholder="Street parking, driveway, etc."
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Move Dates and Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="text-blue-500" />
                Move Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>{t('jobs.collection')} Date</Label>
                  <Input
                    type="date"
                    value={jobData.move_out_date}
                    onChange={(e) => setJobData(prev => ({ ...prev, move_out_date: e.target.value }))}
                    data-testid="move-out-date"
                  />
                </div>
                <div>
                  <Label>{t('jobs.delivery')} Date</Label>
                  <Input
                    type="date"
                    value={jobData.move_in_date}
                    onChange={(e) => setJobData(prev => ({ ...prev, move_in_date: e.target.value }))}
                    data-testid="move-in-date"
                  />
                </div>
                <div>
                  <Label>Preferred Time</Label>
                  <Select 
                    value={jobData.preferred_time}
                    onValueChange={(v) => setJobData(prev => ({ ...prev, preferred_time: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="morning">Morning (8am - 12pm)</SelectItem>
                      <SelectItem value="afternoon">Afternoon (12pm - 5pm)</SelectItem>
                      <SelectItem value="flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>Property Type</Label>
                  <Select 
                    value={jobData.property_details.property_type}
                    onValueChange={(v) => setJobData(prev => ({
                      ...prev,
                      property_details: { ...prev.property_details, property_type: v }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flat">Flat/Apartment</SelectItem>
                      <SelectItem value="house">House</SelectItem>
                      <SelectItem value="bungalow">Bungalow</SelectItem>
                      <SelectItem value="office">Office</SelectItem>
                      <SelectItem value="storage">Storage Unit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Bedrooms</Label>
                  <Select 
                    value={jobData.property_details.bedrooms}
                    onValueChange={(v) => setJobData(prev => ({
                      ...prev,
                      property_details: { ...prev.property_details, bedrooms: v }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="studio">Studio</SelectItem>
                      <SelectItem value="1">1 Bedroom</SelectItem>
                      <SelectItem value="2">2 Bedrooms</SelectItem>
                      <SelectItem value="3">3 Bedrooms</SelectItem>
                      <SelectItem value="4">4 Bedrooms</SelectItem>
                      <SelectItem value="5+">5+ Bedrooms</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Move Type</Label>
                  <Select 
                    value={jobData.customer_type}
                    onValueChange={(v) => setJobData(prev => ({ ...prev, customer_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="domestic">Domestic</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Special Instructions</Label>
                <Textarea
                  value={jobData.special_instructions}
                  onChange={(e) => setJobData(prev => ({ ...prev, special_instructions: e.target.value }))}
                  placeholder="Any special requirements, fragile items, access restrictions..."
                  rows={3}
                  data-testid="special-instructions"
                />
              </div>
            </CardContent>
          </Card>

          {/* Additional Services */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Additional Services</CardTitle>
              <CardDescription>Select any services the customer requires</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { key: 'requires_packing', label: 'Packing Service', icon: '📦' },
                  { key: 'requires_packing_materials', label: 'Packing Materials', icon: '📋' },
                  { key: 'requires_dismantling', label: 'Dismantling/Reassembly', icon: '🔧' },
                  { key: 'requires_storage', label: 'Temporary Storage', icon: '🏢' },
                  { key: 'requires_waste_removal', label: 'Waste Removal', icon: '🗑️' },
                  { key: 'requires_end_of_tenancy', label: 'End-of-Tenancy', icon: '🔑' },
                  { key: 'requires_cleaning', label: 'Cleaning Service', icon: '🧹' },
                ].map(service => (
                  <label 
                    key={service.key}
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                      jobData[service.key] 
                        ? 'bg-primary/10 border-primary' 
                        : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={jobData[service.key] || false}
                      onChange={(e) => setJobData(prev => ({ ...prev, [service.key]: e.target.checked }))}
                      className="sr-only"
                    />
                    <span className="text-lg">{service.icon}</span>
                    <span className="text-sm font-medium">{service.label}</span>
                    {jobData[service.key] && <Check size={16} className="ml-auto text-primary" />}
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Occupancy & Readiness */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Occupancy & Readiness</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm">Property fully packed?</span>
                  <Select 
                    value={jobData.property_packed ? 'yes' : 'no'}
                    onValueChange={(v) => setJobData(prev => ({ ...prev, property_packed: v === 'yes' }))}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm">Someone present at both?</span>
                  <Select 
                    value={jobData.someone_present ? 'yes' : 'no'}
                    onValueChange={(v) => setJobData(prev => ({ ...prev, someone_present: v === 'yes' }))}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm">Keys/codes needed?</span>
                  <Select 
                    value={jobData.keys_codes_required ? 'yes' : 'no'}
                    onValueChange={(v) => setJobData(prev => ({ ...prev, keys_codes_required: v === 'yes' }))}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm">Utilities accessible?</span>
                  <Select 
                    value={jobData.utilities_accessible ? 'yes' : 'no'}
                    onValueChange={(v) => setJobData(prev => ({ ...prev, utilities_accessible: v === 'yes' }))}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {jobData.keys_codes_required && (
                <div className="mt-4">
                  <Label>Access Details</Label>
                  <Input
                    value={jobData.keys_codes_notes}
                    onChange={(e) => setJobData(prev => ({ ...prev, keys_codes_notes: e.target.value }))}
                    placeholder="Key safe code, concierge details, etc."
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)}>
              <ArrowLeft size={16} className="mr-2" />
              Back to Customer Selection
            </Button>
            <Button onClick={handleCreateJob} disabled={loading} data-testid="create-job-submit">
              {loading ? t('common.loading') : t('jobs.create_job')}
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* New Customer Dialog */}
      <Dialog open={showNewCustomer} onOpenChange={setShowNewCustomer}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="text-primary" />
              Create New Customer
            </DialogTitle>
            <DialogDescription>
              Add a new customer to your company
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input
                  value={newCustomer.first_name}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="John"
                  data-testid="new-customer-first-name"
                />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input
                  value={newCustomer.last_name}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Smith"
                  data-testid="new-customer-last-name"
                />
              </div>
            </div>

            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newCustomer.email}
                onChange={(e) => setNewCustomer(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@example.com"
                data-testid="new-customer-email"
              />
            </div>

            <div>
              <Label>Phone</Label>
              <Input
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+44 7700 900000"
                data-testid="new-customer-phone"
              />
            </div>

            <div>
              <Label>Customer Type</Label>
              <Select 
                value={newCustomer.customer_type}
                onValueChange={(v) => setNewCustomer(prev => ({ ...prev, customer_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="domestic">
                    <div className="flex items-center gap-2">
                      <Home size={14} /> Domestic
                    </div>
                  </SelectItem>
                  <SelectItem value="commercial">
                    <div className="flex items-center gap-2">
                      <Building size={14} /> Commercial
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Address (Optional)</Label>
              <AddressAutocomplete
                value={newCustomer.address.street}
                onAddressSelect={(addr) => setNewCustomer(prev => ({
                  ...prev,
                  address: { 
                    street: addr.street,
                    city: addr.city || prev.address.city,
                    postal_code: addr.postal_code || prev.address.postal_code
                  }
                }))}
                onChange={(e) => setNewCustomer(prev => ({
                  ...prev,
                  address: { ...prev.address, street: e.target.value }
                }))}
                placeholder="Start typing address..."
                className="mb-2"
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={newCustomer.address.city}
                  onChange={(e) => setNewCustomer(prev => ({
                    ...prev,
                    address: { ...prev.address, city: e.target.value }
                  }))}
                  placeholder="City"
                />
                <Input
                  value={newCustomer.address.postal_code}
                  onChange={(e) => setNewCustomer(prev => ({
                    ...prev,
                    address: { ...prev.address, postal_code: e.target.value }
                  }))}
                  placeholder="Postal code"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCustomer(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreateCustomer} disabled={loading} data-testid="create-customer-submit">
              {loading ? t('common.loading') : 'Create & Continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateJobPage;
